import { useTranslation } from 'react-i18next';

import type { BillingInfoFormData, BillingInfoFormHandlers } from '@ctct/application-layer';
import { Button, Heading } from '@ctct/payflow-ui';
import { Col, Container, Row } from '@ctct/presentation-layer';
import type { BillingInfoFormHooks } from '@ctct/state-client-layer';

interface Props {
  data: BillingInfoFormData;
  handlers: BillingInfoFormHandlers;
  hooks: BillingInfoFormHooks;
}

function formatName(firstname, lastname) {
  return `${firstname} ${lastname}`;
}

function formatCityState(city, state, countryRequiresStates = true) {
  if (countryRequiresStates) return `${city}, ${state}`;

  return city;
}

export const BillingInfoCard = ({ data, handlers, hooks }: Props) => {
  const { t } = useTranslation();
  const isLoading = hooks.useIsLoading();

  const {
    firstname,
    lastname,
    companyname,
    address1,
    address2,
    city,
    zipcode,
    state,
    country,
    phone,
    email,
    vatNumber,
  } = data.getDefaultValues();

  const countryRequiresStates = data.isShowingStates(country);

  // depends on wether it's mobile or desktop -- we display it in different places
  const phoneAndEmailCol = (
    <Col className="billing-info-card-col contact-details">
      <div>
        <Heading level="h6-xs">{t('billing-info-card.phone').concat(':')}</Heading>
        <p>{phone}</p>
      </div>

      <div>
        <Heading level="h6-xs">{t('billing-info-card.email').concat(':')}</Heading>
        <p className="email-truncated">{email}</p>
      </div>
    </Col>
  );

  return (
    <Container className="billing-info-card-container">
      <Row className="billing-info-card-row">
        <Col>
          <Row>
            <Col className="billing-info-card-col address-details">
              <Heading level="h6-xs">{t('billing-info-card.address').concat(':')}</Heading>
              {[
                formatName(firstname, lastname),
                companyname,
                address1,
                address2,
                formatCityState(city, state, countryRequiresStates),
                zipcode,
                data.getCountryName(country),
                vatNumber,
              ]
                .filter(Boolean)
                .map((el) => (
                  <p key={el}>{el}</p>
                ))}
            </Col>

            {phoneAndEmailCol}
          </Row>
        </Col>
        {!isLoading && (
          <Col fixed>
            <Button variant="text" onClick={handlers.onEditClick}>
              {t('billing-info-form.edit')}
            </Button>
          </Col>
        )}
      </Row>

      <Row className="billing-info-card-row mobile-only">{phoneAndEmailCol}</Row>
    </Container>
  );
};
